import React from "react";
import PersonalisationOptions from "../personalisation-options/personalisation-options";
import PersonalisationCarousel from "../personalisation-carousel/personalisation-carousel";
import PERSONALISATION_DATA from "../../data/personalisation.json";
import gtmPush from "../../modules/gtm";
import { DEMOGRAPHIC_ROWS } from "../personalisation-options/personalisation-options";

const CARDS = [
    {
        label: "How people like you <em>are going with their daily expenses</em>",
        action: "Get your financial wellbeing score",
        actionUrl: "https://www.anz.com.au/personal/financial-wellbeing/calculator/",
        circles: [
            {
                dataKey: "extraMoney",
                valueMatch: "Always",
                label: "say they <em>always have money leftover</em> after bills and necessities",
            },
            {
                dataKey: "bills",
                valueMatch: "Without any difficulty",
                label: "say they <em>pay bills without any difficulty</em>",
            },
        ],
    },
    {
        label: "How people like you <em>plan their spending</em>",
        action: "Plan your spend",
        actionUrl: "https://www.anz.com.au/personal/financial-wellbeing/plan-your-spend/",
        circles: [
            {
                dataKey: "budget",
                valueMatch: "Realistic",
                label: "say they <em>have a realistic budget</em>",
            },
            {
                dataKey: "finTools",
                valueMatch: "No",
                label: "tend <em>not to use financial tools</em>",
            },
        ],
    },
    {
        label: "How people like you <em>manage their debt</em>",
        action: "Manage debt",
        actionUrl: "https://www.anz.com.au/personal/financial-wellbeing/manage-debt/",
        circles: [
            {
                dataKey: "debts",
                valueMatch: "Yes",
                label: "say they <em>have debt</em>",
            },
            {
                dataKey: "debtRepay",
                valueMatch: ["Yes, on some of my debts", "Yes, on all of my debts"],
                label: "tend to <em>make regular payments on debt</em>",
            },
        ],
    },
    {
        label: "How people like you <em>are going with their savings</em>",
        action: "Start saving",
        actionUrl: "https://www.anz.com.au/personal/financial-wellbeing/set-a-savings-goal/",
        circles: [
            {
                dataKey: "savingsGoal",
                valueMatch: "Yes",
                label: "say they <em>have a savings goal</em>",
            },
            {
                dataKey: "savingAbility",
                valueMatch: "It’s a struggle sometimes",
                label:
                    "<em>struggle</em> in <em>achieving</em> their <em>savings goals</em> sometimes",
            },
        ],
    },
    {
        label: "How people like you <em>organise their accounts</em>",
        action: "Start organising",
        actionUrl: "https://www.anz.com.au/personal/financial-wellbeing/organise-your-accounts/",
        circles: [
            {
                dataKey: "buckets",
                valueMatch: "Yes",
                label: "say they <em>don't divide money into 'buckets'</em> for different purposes",
            },
            {
                dataKey: "bankNo",
                valueMatch: "2",
                label: "say they have <em>2 bank accounts</em>",
            },
        ],
    },
    {
        label: "How people like you <em>are going with their super</em>",
        action: "Start investing",
        actionUrl: "https://www.anz.com.au/personal/financial-wellbeing/invest-in-your-future/",
        circles: [
            {
                dataKey: "superNo",
                valueMatch: "1",
                label: "say they have only <em>one super account</em>",
            },
            {
                dataKey: "superAmt",
                valueMatch: "Yes",
                label: "know how <em>much is in their super</em>",
            },
        ],
    },
];

const isValueMatch = (value, valueMatch) =>
    Array.isArray(valueMatch) ? valueMatch.includes(value) : valueMatch === value;

const Personalisation = props => {
    const [options, setOptions] = React.useState(null);
    const [isOpen, setIsOpen] = React.useState(true);

    const handleOptionsSubmitted = React.useCallback(newOpts => {
        setOptions(newOpts);
        setIsOpen(false);
        const demoString = newOpts
            ? Object.entries(newOpts)
                  .filter(([key, value]) => value !== null)
                  .map(
                      ([key, value]) =>
                          `${key}=${DEMOGRAPHIC_ROWS.find(d => d.dataKey === key).values[value]}`
                  )
                  .join(";")
                  .replace(/ /g, "_")
            : "";
        gtmPush({
            event: "personalisation-submit",
            demographics: demoString,
        });
    }, []);
    const handleEditClick = React.useCallback(newOpts => {
        setIsOpen(true);
    }, []);

    const peopleLikeMe = React.useMemo(
        () =>
            options === null
                ? PERSONALISATION_DATA
                : PERSONALISATION_DATA.filter(row => {
                      return Object.entries(options).every(kvp => {
                          const [valueKey, optionValue] = kvp;
                          if (optionValue === null) return true;
                          return row.demographics[valueKey] === optionValue;
                      });
                  }),
        [options]
    );

    const cardsWithData = React.useMemo(() => {
        return CARDS.map((card, index) => {
            const circles = card.circles.map(circle => {
                const matching = peopleLikeMe.filter(row =>
                    isValueMatch(row[circle.dataKey], circle.valueMatch)
                );
                const matchPct = (matching.length / peopleLikeMe.length) * 100;

                return {
                    label: circle.label,
                    value: matchPct || 0,
                    matching,
                };
            });

            const overlap = peopleLikeMe.filter(row => {
                return card.circles.every(circle =>
                    isValueMatch(row[circle.dataKey], circle.valueMatch)
                );
            });
            const overlapPct = (overlap.length / peopleLikeMe.length) * 100;

            return {
                ...card,
                id: "card-" + index,
                circles,
                overlapPct: overlapPct || 0,
            };
        });
    }, [peopleLikeMe]);

    return (
        <div>
            <PersonalisationOptions
                isOpen={isOpen}
                onEditClick={handleEditClick}
                onOptionsSubmitted={handleOptionsSubmitted}
            />
            <PersonalisationCarousel cardData={cardsWithData} isOpen={isOpen} />
        </div>
    );
};

export default Personalisation;
